/* ================================================================
   Text Annotation / Highlighting — Types & localStorage Store
   Persists user highlights + notes attached to assistant messages.
   ================================================================ */

/* ── Types ────────────────────────────────────────────────────── */

export type HighlightColor = 'yellow' | 'green' | 'blue' | 'pink';

export interface Highlight {
  /** Unique id for this highlight */
  id: string;
  /** queryId of the assistant message (may be undefined for loaded sessions) */
  queryId: string | undefined;
  /** Message DOM id (fallback anchor when queryId is absent) */
  messageId: string;
  /** The exact highlighted text */
  text: string;
  /** Character offset from the start of the message's plain-text content */
  startOffset: number;
  /** Character offset at the end */
  endOffset: number;
  /** Optional user note */
  note: string;
  /** Highlight colour */
  color: HighlightColor;
  /** ISO timestamp */
  created_at: string;
  /** Session context hint (first few words of the user query) */
  sessionContext?: string;
}

/* ── localStorage key ─────────────────────────────────────────── */

const LS_KEY = 'standards-highlights';

/* ── CRUD helpers ─────────────────────────────────────────────── */

export function loadHighlights(): Highlight[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveHighlights(highlights: Highlight[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(highlights));
  } catch { /* quota errors ignored */ }
}

export function addHighlight(h: Highlight): void {
  const all = loadHighlights();
  all.unshift(h);
  saveHighlights(all);
}

export function removeHighlight(id: string): void {
  const all = loadHighlights().filter((h) => h.id !== id);
  saveHighlights(all);
}

export function updateHighlightNote(id: string, note: string): void {
  const all = loadHighlights().map((h) =>
    h.id === id ? { ...h, note } : h,
  );
  saveHighlights(all);
}

export function getHighlightsForMessage(messageId: string): Highlight[] {
  return loadHighlights().filter((h) => h.messageId === messageId);
}

export function clearAllHighlights(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
}

/* ── Export as markdown ───────────────────────────────────────── */

export function exportHighlightsMarkdown(highlights: Highlight[]): string {
  const lines = [
    '# Compliance Hub — Highlights & Annotations',
    `Exported: ${new Date().toLocaleString()}`,
    '',
    `Total: ${highlights.length} highlight${highlights.length !== 1 ? 's' : ''}`,
    '',
    '---',
    '',
  ];

  // Group by sessionContext
  const groups = new Map<string, Highlight[]>();
  for (const h of highlights) {
    const key = h.sessionContext || '(No context)';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(h);
  }

  for (const [ctx, items] of groups) {
    lines.push(`## ${ctx}`);
    lines.push('');
    for (const h of items) {
      const date = new Date(h.created_at).toLocaleDateString();
      lines.push(`> ${h.text}`);
      if (h.note) {
        lines.push('');
        lines.push(`**Note:** ${h.note}`);
      }
      lines.push('');
      lines.push(`_${date} · ${h.color}_`);
      lines.push('');
      lines.push('---');
      lines.push('');
    }
  }

  lines.push('');
  lines.push('*Exported from Compliance Hub — research aid only.*');
  return lines.join('\n');
}

/* ── Colour map (CSS values) ──────────────────────────────────── */

export const HIGHLIGHT_COLORS: Record<HighlightColor, { bg: string; border: string; label: string }> = {
  yellow: { bg: 'rgba(250, 204, 21, 0.25)', border: 'rgba(250, 204, 21, 0.6)', label: 'Yellow' },
  green:  { bg: 'rgba(74, 222, 128, 0.25)',  border: 'rgba(74, 222, 128, 0.6)',  label: 'Green' },
  blue:   { bg: 'rgba(96, 165, 250, 0.25)',  border: 'rgba(96, 165, 250, 0.6)',  label: 'Blue' },
  pink:   { bg: 'rgba(244, 114, 182, 0.25)', border: 'rgba(244, 114, 182, 0.6)', label: 'Pink' },
};
